import { Component, OnInit, ViewEncapsulation, LOCALE_ID, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Frequency } from '../frequency';
import { FREQUENCIES } from '../frequency-list';
import { TargetLanguage } from '../target-language';
import { LANGUAGES } from '../target-languages-list';
import { HttpService } from '../service/http.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Vademecum } from '../vademecum';
import { LoginService } from '../security/login.service';
import { BLOODTYPES } from '../blood-type-list';
import { BloodType } from '../blood-type';
import { Title } from '@angular/platform-browser';
import { LocaleService } from '../locale.service';
import { FaviconService } from '../favicon.service';
import { PersonalInfo } from '../personal-info';
import { PersonalInfoService } from '../personal-info.service';
import { MedicalInfoService } from '../medical-info.service';
import { SchoolInfoService } from '../school-info.service';
import { Booking } from '../booking';
import { EASYLANGUAGES_LOGO, LANGUESVIVANTES_LOGO } from '../logo-src-constants';
import { ApiError } from '../api-error';
import { ApiErrorService } from '../api-error.service';
import { Language } from '../language';
import { BrugesFormPayload } from '../bruges-form-payload';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-student-informations',
  templateUrl: './student-informations.component.html',
  styleUrls: ['./student-informations.component.css']
})
export class StudentInformationsComponent implements OnInit {

  schoolForm: FormGroup | undefined;
  medicalForm: FormGroup | undefined;
  frequencies: Frequency[] = FREQUENCIES;
  targetLanguages: TargetLanguage[] = LANGUAGES;
  bloodTypes: BloodType[] = BLOODTYPES;
  uuid: string | null;
  hasError: boolean | undefined;
  years: number[] = [];
  formsInvalid = false;
  booking: Booking | undefined;
  sendingForm = false;
  submitFail = false;

  agreementForm: FormGroup | undefined;

  locale: any;

  personalInfo: PersonalInfo | undefined;
  lviLogo: string;
  testing = true;

  constructor(
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private router: Router,
    @Inject(LOCALE_ID)
    public localeId: string,
    private titleService: Title,
    private localeService: LocaleService,
    private favicon: FaviconService,
    private apiErrorService: ApiErrorService,
    public personalInfoService: PersonalInfoService,
    public medicalInfoService: MedicalInfoService,
    public schoolInfoService: SchoolInfoService
  ) {
    this.loginService.activatedNavigationComponent = false;
    if(this.localeId == "fr"){
      this.lviLogo = LANGUESVIVANTES_LOGO;
    } else {
      this.lviLogo = EASYLANGUAGES_LOGO;
    }
    this.titleService.setTitle(
      this.localeService.setTitleLanguage(this.localeId)
      );
      this.favicon.lvi();
      if(this.route.snapshot.paramMap.get("uuid")){
        this.uuid = this.route.snapshot.paramMap.get("uuid");
      } else {
        this.uuid = null;
      }
   }

  ngOnInit() {
    this.locale = this.localeService.setCalendarLanguage(this.localeId);
    this.agreementForm = this.formBuilder.group({
      agreeWithTerms: [false, Validators.required],
      agreeToTakeStudentPicture: false
    });
    this.makeYears();

    this.httpService.getBookingByUuid(this.uuid!)
    .subscribe(booking => {
      this.booking = booking;
    }, error => {
      if(error.status === 0){
        this.router.navigateByUrl("server-down");
        return;
      } else {
        this.apiErrorService.error = new ApiError(error);
        this.router.navigateByUrl("api-error");
      }
    });
  }

  sendVademecum() {
    this.formsInvalid = false;
    if(!this.medicalInfoService.medicalForm.valid || !this.schoolInfoService.schoolForm.valid || !this.personalInfoService.personalForm.valid){
      this.formsInvalid = true;
      this.submitFail = true;
      window.scroll(0, 0);
      return;
    }
    this.sendingForm = true;
    this.hasError = false;
    let vademecum = new Vademecum(
      this.medicalInfoService.medicalForm.value.streetName.trim(),
      this.medicalInfoService.medicalForm.value.streetNumber.trim(),
      this.medicalInfoService.medicalForm.value.city.trim(),
      this.medicalInfoService.medicalForm.value.postCode.trim(),
      moment(this.medicalInfoService.medicalForm.value.birthDate).utc(true),
      this.medicalInfoService.medicalForm.value.targetLanguage,
      this.medicalInfoService.medicalForm.value.doctorName.trim(),
      this.medicalInfoService.medicalForm.value.doctorPhoneNumber.trim(),
      this.medicalInfoService.medicalForm.value.experiencedDiseases.trim(),
      this.medicalInfoService.medicalForm.value.hasDiabetes,
      this.medicalInfoService.medicalForm.value.hasAsthma,
      this.medicalInfoService.medicalForm.value.hasHeartTrouble,
      this.medicalInfoService.medicalForm.value.hasEpilepsy,
      this.medicalInfoService.medicalForm.value.hasRheumatism,
      this.medicalInfoService.medicalForm.value.hasPhysicalDisability,
      this.medicalInfoService.medicalForm.value.hasSkinAffection,
      this.medicalInfoService.medicalForm.value.hasSleepDisorder,
      this.medicalInfoService.medicalForm.value.otherDiseases.trim(),
      moment(this.medicalInfoService.medicalForm.value.lastSkinTestDate).utc(true),
      this.medicalInfoService.medicalForm.value.skinTestPositive,
      this.medicalInfoService.medicalForm.value.hadTetanusVaccination,
      this.medicalInfoService.medicalForm.value.tetanusBoosterYear,
      this.medicalInfoService.medicalForm.value.hasAllergiesToMedicines,
      this.medicalInfoService.medicalForm.value.allergiesCauseMedicine.trim(),
      this.medicalInfoService.medicalForm.value.hasAllergiesToSubstances,
      this.medicalInfoService.medicalForm.value.allergiesCauseSubstance.trim(),
      this.medicalInfoService.medicalForm.value.isIncontinent,
      this.medicalInfoService.medicalForm.value.isQuicklyTired,
      this.medicalInfoService.medicalForm.value.isSensitiveToChilling,
      this.medicalInfoService.medicalForm.value.canSportWithoutRisk,
      this.medicalInfoService.medicalForm.value.needMedicalTreatment,
      this.medicalInfoService.medicalForm.value.medicinesQuantity.trim(),
      this.medicalInfoService.medicalForm.value.bloodType,
      this.medicalInfoService.medicalForm.value.otherInformations.trim(),
      this.medicalInfoService.medicalForm.value.emergencyContactAddress.trim(),
      this.medicalInfoService.medicalForm.value.hasMentalDisorder,
      this.medicalInfoService.medicalForm.value.mentalDisorderDescription.trim(),
      this.medicalInfoService.medicalForm.value.riskDescriptionToSport.trim(),

      this.schoolInfoService.schoolForm.value.studyDutchInSchool,
      this.schoolInfoService.schoolForm.value.studyEnglishInSchool,
      this.schoolInfoService.schoolForm.value.studyFrenchInSchool,
      this.schoolInfoService.schoolForm.value.dutchStudyYears,
      this.schoolInfoService.schoolForm.value.englishStudyYears,
      this.schoolInfoService.schoolForm.value.frenchStudyYears,
      this.schoolInfoService.schoolForm.value.dutchStudyHours,
      this.schoolInfoService.schoolForm.value.englishStudyHours,
      this.schoolInfoService.schoolForm.value.frenchStudyHours,
      this.schoolInfoService.schoolForm.value.frequencySpeakingWithFamily,
      this.schoolInfoService.schoolForm.value.frequencySpeakingWithFriends,
      this.schoolInfoService.schoolForm.value.hasLearningDifficulty,
      this.schoolInfoService.schoolForm.value.hadLanguageTest,
      moment(this.schoolInfoService.schoolForm.value.languageTestDate).utc(true),
      this.schoolInfoService.schoolForm.value.matterToReview.trim(),
      this.schoolInfoService.schoolForm.value.studyBookName.trim(),
      this.schoolInfoService.schoolForm.value.dutchTeacherName.trim(),
      this.schoolInfoService.schoolForm.value.englishTeacherName.trim(),
      this.schoolInfoService.schoolForm.value.frenchTeacherName.trim(),
      this.schoolInfoService.schoolForm.value.isComingWithFriends,
      this.schoolInfoService.schoolForm.value.friendName.trim(),
      this.booking!,
      this.personalInfoService.personalForm.value.studentMobile.trim(),
      this.personalInfoService.personalForm.value.firstParentMobile.trim(),
      this.personalInfoService.personalForm.value.secondParentMobile.trim(),
      this.personalInfoService.personalForm.value.firstParentFirstName.trim(),
      this.personalInfoService.personalForm.value.firstParentLastName.trim(),
      this.personalInfoService.personalForm.value.secondParentFirstName.trim(),
      this.personalInfoService.personalForm.value.secondParentLastName.trim(),
      undefined,
      this.agreementForm!.value.agreeWithTerms, 
      this.agreementForm!.value.agreeToTakeStudentPicture);

      const payload = this.getBrugesPayload();

      this.httpService.postVademecumV2(
        payload
      ).subscribe(() => {
        console.log("sent successfully");
        this.router.navigateByUrl("dashboard/" + this.uuid);
        window.scroll(0, 0);
        this.sendingForm = false;
      }, error => {
        if(error.status === 0){
          this.router.navigateByUrl("server-down");
          return;
        } else {
          this.apiErrorService.error = new ApiError(error);
          this.router.navigateByUrl("api-error");
        }
        this.hasError = true;
        this.sendingForm = false;
        this.formsInvalid = true;
        window.scroll(0, 0);
      });

    // this.httpService.postVademecum(
    //   vademecum
    // ).subscribe(() => {
    //   console.log("sent successfully");
    //   this.router.navigateByUrl("dashboard/" + this.uuid);
    //   window.scroll(0, 0);
    //   this.sendingForm = false;
    // }, error => {
    //   if(error.status === 0){
    //     this.router.navigateByUrl("server-down");
    //     return;
    //   } else {
    //     this.apiErrorService.error = new ApiError(error);
    //     this.router.navigateByUrl("api-error");
    //   }
    //   this.hasError = true;
    //   this.sendingForm = false;
    //   this.formsInvalid = true;
    //   window.scroll(0, 0);
    // });
  }

  testBackend(){
    let vademecum = this.mockVademecum;
        this.httpService.postVademecumV2(
      this.mockVademecum
    ).subscribe(() => {
      console.log("sent successfully");
      this.router.navigateByUrl("dashboard/" + this.uuid);
      window.scroll(0, 0);
      this.sendingForm = false;
    }, error => {
      if(error.status === 0){
        this.router.navigateByUrl("server-down");
        return;
      } else {
        this.apiErrorService.error = new ApiError(error);
        this.router.navigateByUrl("api-error");
      }
      this.hasError = true;
      this.sendingForm = false;
      this.formsInvalid = true;
      window.scroll(0, 0);
    });
  }

  makeYears(){
    let thisYear = moment().utc(true).year();
    let newYear = thisYear;
    this.years.push(thisYear);
    for(let i = 0; i < 20; i ++){
      newYear -= 1;
      this.years.push(newYear);
    }
  }

  mockVademecum: BrugesFormPayload = {
    streetName: "test street",
    streetNumber: "666",
    city: "test city",
    postCode: "test post code",
    birthDate: moment().utc(true),
    targetLanguage: new Language(2, "English"),
    doctorName: "test doctor name",
    doctorPhoneNumber: "test doctor phone",
    experiencedDiseases: "some diseases",
    hasDiabetes: true,
    hasAsthma: false,
    hasHeartTrouble: true,
    hasEpilepsy: false,
    hasRheumatism: true,
    hasPhysicalDisability: false,
    hasSkinAffection: false,
    hasSleepDisorder: false,
    otherDiseases: null,
    lastSkinTestDate: null,
    skinTestPositive: false,
    hadTetanusVaccination: false,
    tetanusBoosterYear: null,
    hasAllergiesToMedicines: true,
    allergiesCauseMedicine: "some allergies stuff",
    hasAllergiesToSubstances: false,
    allergiesCauseSubstance: null,
    isIncontinent: true,
    isQuicklyTired: false,
    isSensitiveToChilling: true,
    canSportWithoutRisk: false,
    needMedicalTreatment: true,
    medicinesQuantity: null,
    bloodType: new BloodType(5, "lalala"),
    otherInformations: "lalala",
    emergencyContactAddress: "emergency contact",
    hasMentalDisorder: true,
    mentalDisorderDescription: "mental disorder",
    riskDescriptionToSport: null,
    studyDutchInSchool: true,
    studyEnglishInSchool: false,
    studyFrenchInSchool: false,
    dutchStudyYears: 5,
    englishStudyYears: null,
    frenchStudyYears: null,
    dutchStudyHours: 30,
    englishStudyHours: null,
    frenchStudyHours: null,
    frequencySpeakingWithFamily: new Frequency(2, "often"),
    frequencySpeakingWithFriends: new Frequency(1,"never"),
    hasLearningDifficulty: false,
    hadLanguageTest: false,
    languageTestDate: null,
    matterToReview: null,
    studyBookName: null,
    dutchTeacherName: "dutch teacher",
    englishTeacherName: null,
    frenchTeacherName: null,
    isComingWithFriends: false,
    friendName: null,
    uuid: "UUIDqjojdfsjfosdif",
    studentMobile: null,
    firstParentMobile: "0123546",
    secondParentMobile: null,
    firstParentFirstName: "Damien",
    firstParentLastName: "Dupont",
    secondParentFirstName: null,
    secondParentLastName: null,
    agreeWithTerms: true,
    agreeToTakeStudentPicture: true,
    bookingId: 99761
  };

  getBrugesPayload(){
    const medicalForm = this.medicalInfoService.medicalForm;
    const schoolForm = this.schoolInfoService.schoolForm;
    const personalForm = this.personalInfoService.personalForm;
    const agreementForm = this.agreementForm;
    const payload: BrugesFormPayload = {
      streetName: medicalForm.value.streetName.trim(),
      streetNumber: medicalForm.value.streetNumber?medicalForm.value.streetNumber.trim():null,
      city: medicalForm.value.city.trim(),
      postCode: medicalForm.value.postCode.trim(),
      birthDate: moment(medicalForm.value.birthDate).utc(true),
      targetLanguage: medicalForm.value.targetLanguage,
      doctorName: medicalForm.value.doctorName.trim(),
      doctorPhoneNumber: medicalForm.value.doctorPhoneNumber.trim(),
      experiencedDiseases: medicalForm.value.experiencedDiseases?medicalForm.value.experiencedDiseases.trim():null,
      hasDiabetes: medicalForm.value.hasDiabetes,
      hasAsthma: medicalForm.value.hasAsthma,
      hasHeartTrouble: medicalForm.value.hasHeartTrouble,
      hasEpilepsy: medicalForm.value.hasEpilepsy,
      hasRheumatism: medicalForm.value.hasRheumatism,
      hasPhysicalDisability: medicalForm.value.hasPhysicalDisability,
      hasSkinAffection: medicalForm.value.hasSkinAffection,
      hasSleepDisorder: medicalForm.value.hasSleepDisorder,
      otherDiseases: medicalForm.value.otherDiseases?medicalForm.value.otherDiseases.trim():null,
      lastSkinTestDate: medicalForm.value.lastSkinTestDate?moment(medicalForm.value.lastSkinTestDate).utc(true):null,
      skinTestPositive: medicalForm.value.skinTestPositive?medicalForm.value.skinTestPositive:false,
      hadTetanusVaccination: medicalForm.value.hadTetanusVaccination,
      tetanusBoosterYear: medicalForm.value.tetanusBoosterYear,
      hasAllergiesToMedicines: medicalForm.value.hasAllergiesToMedicines,
      allergiesCauseMedicine: medicalForm.value.allergiesCauseMedicine?medicalForm.value.allergiesCauseMedicine.trim():null,
      hasAllergiesToSubstances: medicalForm.value.hasAllergiesToSubstances,
      allergiesCauseSubstance: medicalForm.value.allergiesCauseSubstance?medicalForm.value.allergiesCauseSubstance.trim():null,
      isIncontinent: medicalForm.value.isIncontinent,
      isQuicklyTired: medicalForm.value.isQuicklyTired,
      isSensitiveToChilling: medicalForm.value.isSensitiveToChilling,
      canSportWithoutRisk: medicalForm.value.canSportWithoutRisk,
      needMedicalTreatment: medicalForm.value.needMedicalTreatment,
      medicinesQuantity: medicalForm.value.medicinesQuantity,
      bloodType: medicalForm.value.bloodType,
      otherInformations: medicalForm.value.otherInformations?medicalForm.value.otherInformations.trim():null,
      emergencyContactAddress: medicalForm.value.emergencyContactAddress.trim(),
      hasMentalDisorder: medicalForm.value.hasMentalDisorder,
      mentalDisorderDescription: medicalForm.value.mentalDisorderDescription.trim(),
      riskDescriptionToSport: medicalForm.value.riskDescriptionToSport.trim(),
      studyDutchInSchool: schoolForm.value.studyDutchInSchool,
      studyEnglishInSchool: schoolForm.value.studyEnglishInSchool,
      studyFrenchInSchool: schoolForm.value.studyFrenchInSchool,
      dutchStudyYears: schoolForm.value.dutchStudyYears?schoolForm.value.dutchStudyYears:null,
      englishStudyYears: schoolForm.value.englishStudyYears?schoolForm.value.englishStudyYears:null,
      frenchStudyYears: schoolForm.value.frenchStudyYears?schoolForm.value.frenchStudyYears:null,
      dutchStudyHours: schoolForm.value.dutchStudyHours?schoolForm.value.dutchStudyHours:null,
      englishStudyHours: schoolForm.value.englishStudyHours?schoolForm.value.englishStudyHours:null,
      frenchStudyHours: schoolForm.value.frenchStudyHours?schoolForm.value.frenchStudyHours:null,
      frequencySpeakingWithFamily: schoolForm.value.frequencySpeakingWithFamily,
      frequencySpeakingWithFriends: schoolForm.value.frequencySpeakingWithFriends,
      hasLearningDifficulty: schoolForm.value.hasLearningDifficulty,
      hadLanguageTest: schoolForm.value.hadLanguageTest,
      languageTestDate: schoolForm.value.languageTestDate?moment(schoolForm.value.languageTestDate).utc(true):null,
      matterToReview: schoolForm.value.matterToReview?schoolForm.value.matterToReview.trim():null,
      studyBookName: schoolForm.value.studyBookName?schoolForm.value.studyBookName.trim():null,
      dutchTeacherName: schoolForm.value.dutchTeacherName?schoolForm.value.dutchTeacherName.trim():null,
      englishTeacherName: schoolForm.value.englishTeacherName?schoolForm.value.englishTeacherName.trim():null,
      frenchTeacherName: schoolForm.value.frenchTeacherName?schoolForm.value.frenchTeacherName.trim():null,
      isComingWithFriends: schoolForm.value.isComingWithFriends,
      friendName: schoolForm.value.friendName?schoolForm.value.friendName.trim():null,
      uuid: this.booking?.uuid!,
      studentMobile: personalForm.value.studentMobile.trim(),
      firstParentMobile: personalForm.value.firstParentMobile.trim(),
      secondParentMobile: personalForm.value.secondParentMobile?personalForm.value.secondParentMobile.trim():null,
      firstParentFirstName: personalForm.value.firstParentFirstName.trim(),
      firstParentLastName: personalForm.value.firstParentLastName.trim(),
      secondParentFirstName: personalForm.value.secondParentFirstName?personalForm.value.secondParentFirstName.trim():null,
      secondParentLastName: personalForm.value.secondParentLastName?personalForm.value.secondParentLastName.trim():null,
      agreeWithTerms: agreementForm?.value.agreeWithTerms,
      agreeToTakeStudentPicture: agreementForm?.value.agreeToTakeStudentPicture,
      bookingId: this.booking?.id!
    };
    return payload;
  }
}